import { Link } from 'react-router-dom';

import { useFlashSales, type FlashSale } from '@/hooks/useFlashSales';
import { useConcertBackdrop } from '@/hooks/useConcertBackdrop';
import { Badge, Button, Card, CardTitle } from '@/components/ui';
import { PageBanner } from '@/components/ui/PageBanner';
import { formatCOP, formatEventDate } from '@/lib/format';
import { membershipPlanLabel } from '@/lib/membershipLabels';

/**
 * FlashSales — landing pública de sorteos/promos flash. ROUND17 #9.
 *
 * Data source: `useFlashSales()` → `v_active_flash_sales` (admin R29 B8).
 * Por ahora muestra el listado con countdown + stock + descuento. La
 * integración del CTA "Comprar ahora" con el flow de taquilla queda como
 * sub-iteración cuando admin-lead exponga el endpoint dedicado de compra
 * con descuento aplicado.
 */
export function FlashSalesPage(): JSX.Element {
  const { data: sales, isLoading, isError } = useFlashSales();
  const heroImage = useConcertBackdrop();

  return (
    <>
      <PageBanner
        eyebrow="Ofertas"
        title="Flash Sales"
        subtitle="Promociones por tiempo limitado y cantidad. Aprovechá los descuentos antes de que se terminen."
        heroImage={heroImage}
      />
      <section className="container-fc py-12 md:py-16">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            {[0, 1].map((i) => (
              <Card key={i} variant="outlined" padded>
                <div className="h-5 w-20 rounded bg-neutral-100" />
                <div className="mt-3 h-6 w-3/4 rounded bg-neutral-100" />
                <div className="mt-3 h-4 w-full rounded bg-neutral-100" />
                <div className="mt-5 h-9 w-32 rounded bg-neutral-100" />
              </Card>
            ))}
          </div>
        ) : isError ? (
          <Card variant="outlined" padded>
            <p className="text-fc-muted" role="alert">
              No pudimos cargar las promos en este momento. Probá recargar la página en unos
              minutos.
            </p>
          </Card>
        ) : !sales || sales.length === 0 ? (
          <Card variant="outlined" padded>
            <p className="text-fc-muted">
              Por ahora no hay promos flash activas. Volvé más tarde — se publican periódicamente.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {sales.map((sale) => (
              <FlashSaleCard key={sale.id} sale={sale} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function FlashSaleCard({ sale }: { sale: FlashSale }): JSX.Element {
  const remaining = Math.max(0, sale.quantityAvailable - sale.quantitySold);
  const lowStock =
    sale.quantityAvailable > 0 && remaining <= Math.max(5, sale.quantityAvailable * 0.1);
  return (
    <Card variant="default" padded className="flex h-full flex-col">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <Badge tone="warning">{Math.round(sale.discountPercentage)}% OFF</Badge>
        {lowStock && <Badge tone="danger">Quedan {remaining}</Badge>}
      </div>
      <CardTitle className="mt-3">{sale.title}</CardTitle>
      {sale.description && (
        <p className="mt-2 line-clamp-3 text-sm text-fc-muted">{sale.description}</p>
      )}
      <dl className="mt-4 grid grid-cols-2 gap-y-1 text-xs">
        <dt className="text-fc-muted">Vence</dt>
        <dd className="text-fc-ink">{formatEventDate(sale.endDate)}</dd>
        {sale.maxPerUser !== null && (
          <>
            <dt className="text-fc-muted">Máximo por usuario</dt>
            <dd className="text-fc-ink">{sale.maxPerUser}</dd>
          </>
        )}
        {sale.membershipRequired && (
          <>
            <dt className="text-fc-muted">Membresía requerida</dt>
            {/* ROUND19 #11: traducir slug `first_class` → "FirstClass" legible. */}
            <dd className="text-fc-ink">{membershipPlanLabel(sale.membershipRequired)}</dd>
          </>
        )}
      </dl>
      <div className="mt-5 flex items-center justify-between gap-3">
        <div className="tabular-nums">
          <div className="text-base font-semibold text-fc-ink">{formatCOP(sale.finalPrice)}</div>
          {sale.originalPrice > sale.finalPrice && (
            <div className="text-xs text-fc-muted line-through">
              {formatCOP(sale.originalPrice)}
            </div>
          )}
        </div>
        <Button as={Link} to={`/flash-sales/${sale.id}`} variant="primary" size="sm">
          Ver promo
        </Button>
      </div>
    </Card>
  );
}
